import React, { useState, useEffect, useRef } from "react";
import { object, number } from "prop-types";
import { Subheadline } from "@quintype/arrow";
import { Link } from "@quintype/components";

import StoryElements from "../story-elements";
import CardImage from "../../atoms/cardimage";
import AuthorAndTimeStamp from "../story-header/author-and-timestamp";

import Resizer from "../story-header/resizer";
import FollowUs from "../story-header/follow-us";

import SocialMediaShare from "../../molecules/social-media-share";

import StorySideBar from "../story-sidebar";

import "./exclusive.m.css";
import { get } from "lodash";
import CommentsCount from "../../atoms/comments-count";
import DhExclusiveAuthors from "../story-header/author-and-timestamp/dh-exclusive-author";
import Share from "../../atoms/other-icons/Share";

const DHExclusiveStoryTemplate = ({ story, index, aspectRatio, imageType }) => {
  const isPremiumStory = story?.access === "subscription";
  const isExclusiveWithoutAd = story?.["story-template"] === "exclusive-story-without-ad";
  const shouldApplyHeadlineGap = !isExclusiveWithoutAd || !isPremiumStory;
  const customKicker = get(story, ["metadata", "story-attributes", "customkicker"], "");
  const [showSharingOptions, setShowSharingOptions] = useState(false);
  const shareRef = useRef(null);
  const [isMobile, setMobile] = useState(false);
  const [timestampShareWidth, setTimestampShareWidth] = useState("auto");
  const timestampShareRef = useRef(null);

  useEffect(() => {
    if (window && window.innerWidth < 1024) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  }, []);
  const [isSticky, setIsSticky] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [stickyTop, setStickyTop] = useState(!isMobile ? "80px" : "0px");
  const upwardScrollDeltaRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const authorWrapper = document.querySelector("#exclusive-author-wrapper");
      const mobileHeader = document.querySelector("#mob-header");
      const desktopHeader = document.querySelector("#desk-header");
      const exclusiveAuthorWrapper = document.querySelector("#exclusive-authors-wrapper-id");

      const secondaryHeader =
        document.querySelector("#desk-secondary-header") ||
        document.querySelector(".secondary-menu");
      const isSecondaryHeaderVisible =
        secondaryHeader &&
        parseFloat(window.getComputedStyle(secondaryHeader).opacity) > 0;

      const authorWrapperBottom = authorWrapper?.getBoundingClientRect().bottom;
      let mobileHeaderBottom = 0;
      let desktopHeaderBottom = 0;
      const exclusiveAuthorWrapperBottom = exclusiveAuthorWrapper?.getBoundingClientRect().bottom;

      if (mobileHeader) {
        if (isMobile && mobileHeader.id === "mob-header") {
          const blackBar = Array.from(mobileHeader.children).find(c => c.classList.contains("primary-menu"));
          if (blackBar) {
            const blackBarOpacity = parseFloat(window.getComputedStyle(blackBar).opacity);
            mobileHeaderBottom = blackBarOpacity > 0.95
              ? Math.max(0, blackBar.getBoundingClientRect().bottom)
              : 0;
          }
        }
      }

      if (desktopHeader && !isMobile) {
        desktopHeaderBottom = desktopHeader.getBoundingClientRect().bottom || 0;
        if (secondaryHeader && isSecondaryHeaderVisible) {
          desktopHeaderBottom = desktopHeaderBottom + secondaryHeader.offsetHeight;
        }
      }

      if (typeof window !== "undefined") {
        const currentScrollY = window.scrollY;
        const isScrollingUp = currentScrollY < lastScrollY;
        const scrollDelta = lastScrollY - currentScrollY;
        if (isScrollingUp) {
          upwardScrollDeltaRef.current += scrollDelta;
        } else {
          upwardScrollDeltaRef.current = 0;
        }
        setLastScrollY(currentScrollY);

        const taboolaAdWrapper = document.querySelector("#ad-wrapper-taboola");
        const followUsCard = document.querySelector("#follow-us-card");

        if (followUsCard) {
          let followUsCardTop = followUsCard.getBoundingClientRect().top + 120;

          const authorWrapperHeight = authorWrapper?.getBoundingClientRect().height || 0;

          if (followUsCardTop <= authorWrapperHeight) {
            setIsSticky(false);
            return;
          }
        }

        if (taboolaAdWrapper) {
          let taboolaAdTop = taboolaAdWrapper.getBoundingClientRect().top + 100;
          const authorWrapperHeight = authorWrapper?.getBoundingClientRect().height || 0;

          if (taboolaAdTop <= authorWrapperHeight) {
            setIsSticky(false);
            return;
          }
        }

        if (isMobile) {
          if (isScrollingUp && mobileHeaderBottom >= authorWrapperBottom + 41) {
            setIsSticky(true);
            const effectiveStickyTop = authorWrapperBottom < -100 ? 0 : mobileHeaderBottom;
            setStickyTop(`${effectiveStickyTop}px`);
          } else if (!isScrollingUp || mobileHeaderBottom < authorWrapperBottom + 41) {
            setIsSticky(false);
          }
        } else {
          if (isScrollingUp && upwardScrollDeltaRef.current >= 30 && desktopHeaderBottom >= authorWrapperBottom + (exclusiveAuthorWrapperBottom - authorWrapperBottom) + 20) {
            setIsSticky(true);
            setStickyTop(`${desktopHeaderBottom}px`);
          } else if (!isScrollingUp || (!isMobile && !isSecondaryHeaderVisible) || desktopHeaderBottom < authorWrapperBottom + (exclusiveAuthorWrapperBottom - authorWrapperBottom) + 20) {
            setIsSticky(false);
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, setIsSticky, lastScrollY]);

  const sharingHandler = () => {
    setShowSharingOptions(!showSharingOptions);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSharingOptions && !shareRef.current.contains(event.target)) {
        setShowSharingOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSharingOptions]);

  useEffect(() => {
    if (timestampShareRef.current) {
      setTimestampShareWidth(`${timestampShareRef.current.offsetWidth}px`);
    }
  }, [isSticky]);

  const CommentAndShareBtn = (
    <div styleName="comment-share-wrapper">
      <CommentsCount story={story} showCommentText={false} renderPortal={true} />
      <div styleName="share" ref={shareRef}>
        <button onClick={sharingHandler}>
          <Share color="black" height="36px" width="36px" />
        </button>
        <div styleName="share-options">{showSharingOptions && <SocialMediaShare story={story} />}</div>
      </div>
    </div>
  );

  return (
    <>
      <div styleName="wrapper" className={`story-section ${isExclusiveWithoutAd ? "no-separator" : ""}`}>
        <div>
          <div styleName="header-content">
            <CardImage
              story={story}
              aspectRatio={aspectRatio}
              imageType={imageType}
              showVideoIcon={true}
              widths={[400, 1300]}
              sizes="(max-width: 1024px) 10vw, 100vw"
            />

            <div styleName={`headline-wrapper ${shouldApplyHeadlineGap ? "headline-gap" : ""}`} id="exclusive-author-wrapper">
              {customKicker.length > 0 ? <div styleName="custom-kicker">{customKicker}</div> : null}
              {isPremiumStory && (
                <Link href="/premium" aria-label="Premium">
                  <div styleName="premiumBadge">
                    <img
                      src="https://thumbor-stg.assettype.com/prajavani2/2026-05-26/vz8c16hs/Small.svg"
                      alt="Premium"
                      styleName="premiumBadgeIcon"
                    />
                    <span styleName="premiumBadgeText">PREMIUM</span>
                  </div>
                </Link>
              )}
              <h1>{story.headline}</h1>
              <Subheadline lineClamp={10} story={story} clazzName="sub-headline" />
            </div>
            <div
              id="exclusive-authors-wrapper-id"
              styleName="exclusive-author-wrapper"
              className={`exclusive-sticky-div`}
              ref={timestampShareRef}
              style={{ width: timestampShareWidth }}
            >
              <DhExclusiveAuthors story={story} />
            </div>
          </div>
          <div styleName={`sub-content ${isExclusiveWithoutAd ? "sub-content-center" : ""}`}>
            <div styleName={`story-content ${isExclusiveWithoutAd ? "story-content-center" : ""}`}>
              <div styleName="content">
                <div
                  className={`exclusive-sticky-div ${isSticky && !isMobile ? "exclusive-sticky" : ""}`}
                  style={{ top: isSticky && !isMobile ? stickyTop : "auto" }}
                >
                  <AuthorAndTimeStamp story={story} isDHExclusive={true} isSticky={isSticky} />
                </div>
                <StoryElements story={story} cards={story.cards} index={index} />
              </div>
            </div>
            {!isExclusiveWithoutAd && (
              <div className="hide-mobile side-story-section">
                <StorySideBar index={index} story={story} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

DHExclusiveStoryTemplate.propTypes = {
  story: object,
  index: number
};

DHExclusiveStoryTemplate.defaultProps = {
  aspectRatio: [16, 9],
  imageType: "image16x9"
};

const ExclusiveStory = (props) => {
  return <DHExclusiveStoryTemplate story={props.story} index={props.index} />;
};

ExclusiveStory.propTypes = {
  story: object,
  index: number
};

export default ExclusiveStory;
